import { IMacroExam, IMedicalService, IMedicalServiceBaseInfo } from 'interfaces';
import { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactFinalWithInitialValuesForm } from 'components/form/Dialog/ReactFinalDialogForm';
import { IdentityDialogField } from 'components/form/Dialog/IdentityDialogField';
import { TextFieldDialogAdapter } from 'components/form/Dialog/TextFieldDialogAdapter';
import { AutocompleteDialogAdapter } from 'components/form/Dialog/AutocompleteDialogAdapter';
import { composeValidators, mustBeNumber } from 'helpers/validators';

const FORM_ID = 'MedicalServiceBaseInfoForm';

type MedicalServiceBaseInfoFormValues = {
  medical_service?: number;
  macro_exam?: number;
  caption?: string;
  sector?: string;
  invoicing_code?: string;
  invoicing_code_description?: string;
  asl_internal_codes?: string;
  unit_price?: number;
};

interface Props {
  onSubmit: (values: MedicalServiceBaseInfoFormValues, medicalServiceBaseInfoID?: number) => void;
  medicalServiceBaseInfo?: IMedicalServiceBaseInfo;
  macroExams: IMacroExam[];
}

function HalfRow({ children }: { children: ReactNode }) {
  return <div className="w-1/2 mb-4 pr-4 flex">{children}</div>;
}

function Row({ left, right }: { left: ReactNode; right: ReactNode }) {
  return (
    <div className="flex flex-wrap">
      <HalfRow>{left}</HalfRow>
      <HalfRow>{right}</HalfRow>
    </div>
  );
}

type BaseField<Name extends keyof MedicalServiceBaseInfoFormValues> = {
  name: Name;
  label: string;
};

type TextField<Name extends keyof MedicalServiceBaseInfoFormValues> = BaseField<Name> & {
  kind: 'text';
  type: 'text' | 'number';
  validate?: (value: unknown) => string | undefined;
};

type AutocompleteField<Name extends keyof MedicalServiceBaseInfoFormValues> = BaseField<Name> & {
  kind: 'autocomplete';
  options: IMacroExam[] | IMedicalService[];
  optionValue: 'id';
  optionLabel: 'code' | 'id';
};

type FieldConfig<
  Name extends keyof MedicalServiceBaseInfoFormValues = keyof MedicalServiceBaseInfoFormValues
> = TextField<Name> | AutocompleteField<Name>;

type FieldsMap = {
  macroExam: AutocompleteField<'macro_exam'>;
  caption: TextField<'caption'>;
  sector: TextField<'sector'>;
  invoicingCode: TextField<'invoicing_code'>;
  invoicingCodeDesc: TextField<'invoicing_code_description'>;
  asl: TextField<'asl_internal_codes'>;
  unitPrice: TextField<'unit_price'>;
};

function RenderField({ field }: { field: FieldConfig }) {
  switch (field.kind) {
    case 'autocomplete':
      return (
        <IdentityDialogField
          {...({
            name: String(field.name),
            component: AutocompleteDialogAdapter,
            options: field.options,
            optionValue: field.optionValue,
            optionLabel: field.optionLabel,
            label: field.label
          } as any)}
        />
      );

    case 'text':
      return (
        <IdentityDialogField
          {...({
            name: String(field.name),
            component: TextFieldDialogAdapter,
            type: field.type,
            label: field.label,
            validate: field.validate
          } as any)}
        />
      );

    default: {
      const _never: never = field;
      return null;
    }
  }
}

export default function MedicalServiceBaseInfoForm({
  onSubmit,
  medicalServiceBaseInfo,
  macroExams
}: Props) {
  const { t } = useTranslation();
  const translationBaseInfoPrefix = 'entities.medical_services_base_info';

  const baseInfoT = useCallback(
    (suffix: string) => t(`${translationBaseInfoPrefix}.${suffix}`),
    [t, translationBaseInfoPrefix]
  );

  const initialValues = useMemo<MedicalServiceBaseInfoFormValues>(
    () => ({
      macro_exam: medicalServiceBaseInfo?.macro_exam_id,
      caption: medicalServiceBaseInfo?.caption ?? '',
      sector: medicalServiceBaseInfo?.sector ?? '',
      invoicing_code: medicalServiceBaseInfo?.invoicing_code ?? '',
      invoicing_code_description: medicalServiceBaseInfo?.invoicing_code_description ?? '',
      asl_internal_codes: medicalServiceBaseInfo?.asl_internal_codes ?? '',
      unit_price: medicalServiceBaseInfo?.unit_price ?? undefined
    }),
    [medicalServiceBaseInfo]
  );

  const fields = useMemo<FieldsMap>(
    () => ({
      macroExam: {
        kind: 'autocomplete',
        name: 'macro_exam',
        label: baseInfoT('macro_exam'),
        options: macroExams,
        optionValue: 'id',
        optionLabel: 'code'
      },

      caption: {
        kind: 'text',
        name: 'caption',
        label: baseInfoT('caption'),
        type: 'text'
      },

      sector: {
        kind: 'text',
        name: 'sector',
        label: baseInfoT('sector'),
        type: 'text'
      },

      invoicingCode: {
        kind: 'text',
        name: 'invoicing_code',
        label: baseInfoT('invoicing_code'),
        type: 'text'
      },

      invoicingCodeDesc: {
        kind: 'text',
        name: 'invoicing_code_description',
        label: baseInfoT('invoicing_code_description'),
        type: 'text'
      },

      asl: {
        kind: 'text',
        name: 'asl_internal_codes',
        label: baseInfoT('asl_internal_codes'),
        type: 'text'
      },

      unitPrice: {
        kind: 'text',
        name: 'unit_price',
        label: baseInfoT('unit_price'),
        type: 'number',
        validate: composeValidators(mustBeNumber)
      }
    }),
    [baseInfoT, macroExams]
  );

  return (
    <ReactFinalWithInitialValuesForm
      onSubmit={(values) => onSubmit(values, medicalServiceBaseInfo?.id)}
      hideControls
      formId={FORM_ID}
      initialValues={initialValues}>
      <div id="FormChildren">
        <div className="mb-4 pr-4">
          <RenderField field={fields.macroExam} />
        </div>

        <Row
          left={<RenderField field={fields.caption} />}
          right={<RenderField field={fields.sector} />}
        />

        <Row
          left={<RenderField field={fields.invoicingCode} />}
          right={<RenderField field={fields.invoicingCodeDesc} />}
        />

        <Row
          left={<RenderField field={fields.asl} />}
          right={<RenderField field={fields.unitPrice} />}
        />
      </div>
    </ReactFinalWithInitialValuesForm>
  );
}
